import Sortable from "sortablejs";

export default function registerPageEditor(Alpine) {
  Alpine.data("pageEditor", (config) => ({
    slug: config.slug,
    published: config.published,
    sections: config.sections.map((s, i) => ({
      ...s,
      _key: s.id || `new_${i}`,
      _collapsed: false
    })),
    sectionTemplates: config.sectionTemplates || {},
    activeLocale: "en",
    sortable: null,

    sectionTypeLabels: {
      html_1col: "Single column",
      html_2col: "Two columns",
      html_3col: "Three columns",
      contact_form: "Contact form",
      image: "Image",
      text_image: "Text + Image"
    },

    categoryLabels: {
      hero: "Hero",
      features: "Features",
      cta: "Call to Action",
      content: "Content",
      columns: "Columns",
      contact: "Contact",
      media: "Media"
    },

    init() {
      this.$nextTick(() => this.setupSortable());
    },

    sectionTypeLabel(type) {
      return this.sectionTypeLabels[type] || type;
    },

    _expandedBeforeDrag: [],

    setupSortable() {
      const list = this.$refs.sectionsList;
      if (!list || this.sortable) return;

      this.sortable = Sortable.create(list, {
        handle: ".drag-handle",
        animation: 150,
        ghostClass: "sortable-ghost",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        onStart: () => {
          // Remember which sections were expanded, then collapse all
          this._expandedBeforeDrag = this.sections.map((s) => !s._collapsed);
          this.sections.forEach((s) => (s._collapsed = true));
        },
        onEnd: (evt) => {
          // Revert DOM move — let Alpine handle re-rendering from data
          const { oldIndex, newIndex, item } = evt;
          if (oldIndex !== newIndex) {
            // Put DOM element back so Alpine stays in sync
            const parent = item.parentNode;
            const children = [...parent.children].filter((el) => el.tagName !== "TEMPLATE");
            if (oldIndex < newIndex) {
              parent.insertBefore(item, children[oldIndex]);
            } else {
              parent.insertBefore(item, children[oldIndex + 1] || null);
            }

            // Update data array — Alpine will re-render correctly
            const moved = this.sections.splice(oldIndex, 1)[0];
            this.sections.splice(newIndex, 0, moved);
          }

          // Restore expanded/collapsed state
          this.$nextTick(() => {
            // Reorder the saved states to match the new order
            if (this._expandedBeforeDrag.length) {
              const reordered = [...this._expandedBeforeDrag];
              if (oldIndex !== newIndex) {
                const movedState = reordered.splice(oldIndex, 1)[0];
                reordered.splice(newIndex, 0, movedState);
              }
              this.sections.forEach((s, i) => (s._collapsed = !reordered[i]));
            }
            this._expandedBeforeDrag = [];
          });
        }
      });
    },

    getLocalized(section, field, locale) {
      return section.localized_meta?.[field]?.[locale] || "";
    },

    setLocalized(section, field, locale, value) {
      if (!section.localized_meta) section.localized_meta = {};
      if (!section.localized_meta[field]) section.localized_meta[field] = {};
      section.localized_meta[field][locale] = value;
    },

    setData(section, key, value) {
      if (!section.data) section.data = {};
      section.data[key] = value;
    },

    categoryLabel(cat) {
      return this.categoryLabels[cat] || cat;
    },

    templateIcon(icon) {
      const icons = {
        rocket:
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" /></svg>',
        photo:
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5a2.25 2.25 0 0 0 2.25-2.25V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" /></svg>',
        grid: '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" /></svg>',
        list: '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>',
        megaphone:
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46" /></svg>',
        "arrow-right":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg>',
        "document-text":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>',
        "question-mark-circle":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" /></svg>',
        "view-columns":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 4.5v15m6-15v15M3.75 4.5h16.5" /></svg>',
        "squares-2x2":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 4.5v15m4.5-15v15m4.5-15v15M3.75 4.5h16.5" /></svg>',
        envelope:
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>',
        phone:
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" /></svg>',
        "rectangle-group":
          '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z" /></svg>'
      };
      return icons[icon] || icons["document-text"];
    },

    addFromTemplate(tpl) {
      this.sections.push({
        _key: `new_${Date.now()}`,
        _collapsed: false,
        id: null,
        section_type: tpl.section_type,
        position: this.sections.length,
        data: JSON.parse(JSON.stringify(tpl.default_data || {})),
        localized_meta: JSON.parse(JSON.stringify(tpl.default_localized_meta || {})),
        image_url: null
      });
    },

    removeSection(index) {
      this.sections.splice(index, 1);
    },

    async uploadImage(section, event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!section.id) {
        alert("Please save the page first before uploading images.");
        return;
      }

      const formData = new FormData();
      formData.append("image", file);
      formData.append("section_id", section.id);

      const response = await fetch(config.uploadUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": config.csrfToken },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        section.image_url = data.image_url;
      }
    },

    async removeImage(section) {
      if (!section.id) return;

      const response = await fetch(config.uploadUrl.replace("upload_section_image", "remove_section_image"), {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": config.csrfToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ section_id: section.id })
      });

      if (response.ok) {
        section.image_url = null;
      }
    },

    prepareSections() {
      const payload = this.sections.map((section, index) => ({
        id: section.id,
        section_type: section.section_type,
        position: index,
        data: section.data || {},
        localized_meta: section.localized_meta || {}
      }));
      this.$refs.sectionsJson.value = JSON.stringify(payload);
    }
  }));
}
